import React, { useState, Suspense } from 'react';
import { Page, Card } from '@nearst/ui';
import { useChannel } from '@services/channels';
import { useShop } from '@services/ShopProvider';
import DatePicker from '@components/DatePicker';
import HideForLocalFeedPartner from '@utils/HideForLocalFeedPartner';

import PerformanceOverTime from '../ProductLocator/components/PerformanceOverTime';
import EventLocations from '../ProductLocator/components/EventLocations';
import TopStores from '../ProductLocator/components/TopStores';
import ProductPageLinkingAnalytics from '../SWIS/ProductPageLinkingAnalytics';
import PopularProductsCard from '../components/PopularProductsCard';
import { AdsImpactContent } from '../../reports/AdsImpact';
import OrganicVisibilityCard from '../components/OrganicVisibilityCard';
import { useHasAdsInPeriod } from '../../../hooks/useHasAdsInPeriod';
import ReportFilters from '../../reports/components/ReportFilters';
import LocationFilter from '../components/LocationFilter';
import OptionsButton from '../../reports/components/OptionsButton';
import LocalSearchTermsCard from '../LocalAds/InsightCards/LocalSearchTermsCard';
import PopularProductsChain from '../LocalListingsChain/PopularProductsChain';
import LiaPerformanceCard from '../components/LiaPerformanceCard';

import styles from './Overview.module.scss';

const ProductLocatorInsights = () => {
	const { data: channelConfig } = useChannel('product-locator');

	return channelConfig?.status === 'enabled' ? (
		<>
			<h3>Product Locator</h3>
			<PerformanceOverTime />
			<div className={styles.splitBigSmall}>
				<EventLocations />
				<TopStores />
			</div>
		</>
	) : null;
};

const ProductPageLinksInsights = () => {
	const { data: shops } = useShop();
	return shops.some((shop) => shop.useProductLink) ? (
		<>
			<h3>Product Page Links</h3>
			<ProductPageLinkingAnalytics shops={shops} />
		</>
	) : null;
};

const LocalAdsPopularProducts = () => {
	const { data: shops } = useShop();
	const hasAdsInPeriod = useHasAdsInPeriod(shops.map((shop) => shop.id.toString()));

	if (!hasAdsInPeriod) {
		return null;
	}
	return (
		<>
			<h3>Local Inventory Ads Popular Products</h3>
			<div className={styles.container}>
				<PopularProductsCard />
			</div>
		</>
	);
};

const LocalSearchTerms = () => {
	const { data: shops } = useShop();
	const hasAdsInPeriod = useHasAdsInPeriod(shops.map((shop) => shop.id.toString()));

	if (!hasAdsInPeriod) {
		return null;
	}
	return (
		<>
			<h3>Local Search Terms</h3>
			<div className={styles.container}>
				<Suspense fallback={<Card.Loader message="Loading local search terms..." />}>
					<LocalSearchTermsCard />
				</Suspense>
			</div>
		</>
	);
};

const LocalAdsImpact = () => {
	const { data: shops } = useShop();

	const [shopIds, setShopIds] = useState(shops.map((shop) => shop.id.toString()));
	const [options, setOptions] = useState({
		dataLabels: true,
		referenceLines: true,
		summaryView: false
	});

	const hasAdsInPeriod = useHasAdsInPeriod(shops.map((shop) => shop.id.toString()));

	if (!hasAdsInPeriod) {
		return null;
	}

	return (
		<>
			<h3>Local Inventory Ads Impact</h3>
			<div className={styles.filtersBox}>
				<ReportFilters>
					<LocationFilter shops={shops} selectedShops={shopIds} setSelectedShops={setShopIds} />
					<OptionsButton
						options={[
							{
								id: 'dataLabels',
								label: 'Data labels'
							},
							{
								id: 'referenceLines',
								label: 'Average lines'
							},
							{
								id: 'summaryView',
								label: 'Summary view'
							}
						]}
						values={options}
						setValues={setOptions}
					/>
				</ReportFilters>
			</div>
			<Card sectioned>
				<Suspense fallback={<Card.Loader message="Generating report..." />}>
					<AdsImpactContent options={options} shopIds={shopIds} />
				</Suspense>
			</Card>
		</>
	);
};

const LocalAdsPerformance = () => {
	const { data: shops } = useShop();
	const hasAdsInPeriod = useHasAdsInPeriod(shops.map((shop) => shop.id.toString()));

	if (!hasAdsInPeriod) {
		return null;
	}
	return (
		<>
			<h3>Local Ads Performance</h3>
			<div className={styles.container}>
				<LiaPerformanceCard shops={shops} />
			</div>
		</>
	);
};

const LocalListingsPopularProducts = () => {
	const { data: shops } = useShop();
	const hasAdsInPeriod = useHasAdsInPeriod(shops.map((shop) => shop.id.toString()));

	if (hasAdsInPeriod) {
		return null;
	}

	return (
		<HideForLocalFeedPartner>
			<h3>Local Listings Popular Products</h3>
			<div className={styles.container}>
				<Suspense fallback={<Card.Loader message="Loading popular products..." />}>
					<PopularProductsChain />
				</Suspense>
			</div>
		</HideForLocalFeedPartner>
	);
};

const OrganicVisibilityInsights = () => {
	const { data: shops } = useShop();

	const [shopIds, setShopIds] = useState(shops.map((shop) => shop.id.toString()));
	const [options, setOptions] = useState({
		dataLabels: true,
		referenceLines: true,
		summaryView: false
	});

	return (
		<>
			<h3>Organic Visibility</h3>
			<div className={styles.filtersBox}>
				<ReportFilters>
					<LocationFilter shops={shops} selectedShops={shopIds} setSelectedShops={setShopIds} />
					<OptionsButton
						options={[
							{
								id: 'dataLabels',
								label: 'Data labels'
							},
							{
								id: 'referenceLines',
								label: 'Average lines'
							},
							{
								id: 'summaryView',
								label: 'Summary view'
							}
						]}
						values={options}
						setValues={setOptions}
					/>
				</ReportFilters>
			</div>
			<div className={styles.container}>
				<Suspense fallback={<Card.Loader message="Loading organic visibility..." />}>
					<OrganicVisibilityCard options={options} shopIds={shopIds} />
				</Suspense>
			</div>
		</>
	);
};

const Overview = () => {
	return (
		<>
			<Page.Header>
				<h1>Overview</h1>
				<p>Get a comprehensive view of your insights across all channels and locations.</p>
				<DatePicker />
			</Page.Header>
			<Page.Section>
				<OrganicVisibilityInsights />
				<LocalAdsImpact />
				<LocalAdsPerformance />
				<LocalAdsPopularProducts />
				<LocalSearchTerms />
				<ProductLocatorInsights />
				<ProductPageLinksInsights />
				<LocalListingsPopularProducts />
			</Page.Section>
		</>
	);
};

export default Overview;

